import { datadogRum } from "@datadog/browser-rum";
import { createJourneyEventBuffer } from "./journeyEventBuffer";
import { generateClientJourneyId } from "./clientJourneyId";
import { buildJourneyEvent } from "./journeyEventBuilder";
import { JourneyStepType } from "./journeyStepTypes";

/**
 * Module-level singleton so journey events can be emitted consistently from
 * anywhere in the app — not just React components wrapped by
 * `withJourneyMonitoring` (e.g. redux-saga business logic for benefits
 * lookups and rescue submission/cancellation, which run outside React's
 * render tree and have no access to hooks/context).
 *
 * There is exactly one buffer/clientJourneyId per app load, matching the
 * single `withJourneyMonitoring` instance that previously owned this state
 * (App.js is wrapped exactly once), so moving it to a singleton changes
 * nothing about the "one journey per app session" semantics.
 */
const buffer = createJourneyEventBuffer(20);
const clientJourneyId = generateClientJourneyId();

interface JourneyIdentifiers {
  sessionId?: string;
  callId?: string;
}

let identifiers: JourneyIdentifiers = { sessionId: undefined, callId: undefined };

// DEVELOPER NOTE: if true, you will see console logs of DD actions set. Should always be false in production.
// You also need to set ALLOW_LOCAL_DATADOG to true in App.js
const DEBUG_LOG_JOURNEY_EVENTS = false;

const isProduction = (): boolean =>
  typeof process !== "undefined" && !!process.env && process.env.NODE_ENV === "production";

const warnInDev = (...args: unknown[]): void => {
  if (!isProduction() && typeof console !== "undefined") {
    // eslint-disable-next-line no-console
    console.warn("[journeyMonitoringService]", ...args);
  }
};

/** Updates the sessionId/callId used to correlate subsequently emitted events. */
export const setJourneyIdentifiers = (nextIdentifiers: JourneyIdentifiers = {}): void => {
  identifiers = { ...identifiers, ...nextIdentifiers };
};

export const getJourneyIdentifiers = () => ({ ...identifiers, clientJourneyId });

export const getJourneyBufferSnapshot = () => buffer.getAll();

/**
 * Builds a JourneyEvent, appends it to the shared buffer, 
 * and forwards it to Datadog. Never throws — a failure here 
 * must never block business logic or crash the app.
 */
export const emitJourneyEvent = (
  stepType: JourneyStepType,
  stepName: string,
  rawMetadata?: Record<string, unknown>
): void => {
  try {
    const event = buildJourneyEvent(stepType, stepName, rawMetadata, {
      sessionId: identifiers.sessionId,
      callId: identifiers.callId,
      clientJourneyId,
    });
    if (!event) {
      return;
    }
    buffer.push(event);
    // Searchable, no-colon/bracket action name: journey-monitoring_<callId or clientJourneyId fallback>_<stepName>
    // e.g. @action.name:journey-monitoring_call-abc123_benefits.found in Datadog.
    const actionIdentifier = event.callId || event.clientJourneyId;
    const actionName = `journey-monitoring_${actionIdentifier}_${event.stepName}`;
    if (DEBUG_LOG_JOURNEY_EVENTS) {
      // eslint-disable-next-line no-console
      console.log(actionName, event.stepType, event);
    }
    datadogRum.addAction(actionName, event);
  } catch (error) {
    warnInDev("Failed to emit journey event; continuing without it.", error);
  }
};
